import { useEffect } from "react";

/**
 * Tracks input modality (keyboard vs pointer) globally
 * Sets data-modality attribute on documentElement
 *
 * This ensures focus rings only appear during keyboard navigation,
 * not when Radix UI programmatically manages focus after mouse clicks.
 */
export function useInputModality() {
  useEffect(() => {
    let isKeyboardMode = false;

    const handleKeyDown = (e) => {
      // Tab, Arrow keys, Enter, Space indicate keyboard navigation
      // Guard against undefined e.key (can happen with synthetic events or extensions)
      if (
        e.key === "Tab" ||
        e.key?.startsWith("Arrow") ||
        e.key === "Enter" ||
        e.key === " "
      ) {
        if (!isKeyboardMode) {
          isKeyboardMode = true;
          document.documentElement.dataset.modality = "keyboard";
        }
      }
    };

    const handlePointerDown = () => {
      if (isKeyboardMode) {
        isKeyboardMode = false;
        document.documentElement.dataset.modality = "pointer";
      }
    };

    // Start in pointer mode
    document.documentElement.dataset.modality = "pointer";

    // Use capture phase to ensure we catch events before they reach components
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, []);
}
